import MoneyInput from '../../components/MoneyInput';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { page, wrap, card, h1, h2, muted, grid, label, input, row, bigRow, backBtn, btn, money, num } from './ui';
import ShareReport from '../../components/ShareReport';
import { mortgagePayment } from '../../shared/calculators';

export default function MortgageCalculator() {
  const navigate = useNavigate();
  const [v, setV] = useState({ price: '', downPct: '20', rate: '', years: '30', tax: '', insurance: '2500', hoa: '', hoaPeriod: 'month', cdd: '', pmiPct: '0.5' });
  const [rateInfo, setRateInfo] = useState({ status: 'loading', asOf: '' });
  const [extras, setExtras] = useState([]);
  const [newExtra, setNewExtra] = useState({ label: '', amount: '' });
  const addExtra = () => {
    if (!newExtra.label.trim() || !newExtra.amount) return;
    setExtras([...extras, { id: Date.now(), label: newExtra.label.trim(), amount: newExtra.amount }]);
    setNewExtra({ label: '', amount: '' });
  };
  const removeExtra = (id) => setExtras(extras.filter((e) => e.id !== id));
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  // Fill in the current 30-year fixed rate (Freddie Mac weekly survey). The agent can still type over it.
  useEffect(() => {
    const url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/mortgageRate30`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('rate'))))
      .then((data) => {
        setV((prev) => (prev.rate === '' ? { ...prev, rate: String(data.rate) } : prev));
        setRateInfo({ status: 'ok', asOf: data.asOf });
      })
      .catch(() => setRateInfo({ status: 'unavailable', asOf: '' }));
  }, []);

  // Same math as the MCP mortgage_calculator tool (src/shared/calculators.js).
  const { price, down, loan, pi, taxMo, insMo, hoaMo, cddMo, pmiMo, total, rateMissing, totalInterest, cashToClose } = mortgagePayment(v, extras);

  return (
    <div style={page}>
      <div style={wrap}>
        <button onClick={() => navigate('/tools')} style={backBtn}>← Back to tools</button>
        <h1 style={h1}>Monthly Payment Calculator</h1>
        <p style={muted}>Estimated monthly payment. Rates, taxes, insurance, and PMI vary by lender and property.</p>

        <div style={card}>
          <h2 style={h2}>Loan details</h2>
          <div style={grid}>
            <div><span style={label}>Purchase price</span><MoneyInput style={input} value={v.price} onChange={set('price')} placeholder="650000" /></div>
            <div><span style={label}>Down payment %</span><input style={input} type="number" step="0.1" value={v.downPct} onChange={set('downPct')} /></div>
            <div><span style={label}>Interest rate % (30-year fixed)</span><input style={input} type="number" step="0.01" value={v.rate} onChange={set('rate')} placeholder={rateInfo.status === 'loading' ? 'Loading current rate...' : '6.50'} />{rateInfo.status === 'ok' && <span style={muted}>Current Freddie Mac average as of {rateInfo.asOf}. You can change it.</span>}{rateInfo.status === 'unavailable' && <span style={muted}>Could not load the current rate. Enter it manually.</span>}</div>
            <div><span style={label}>Loan term (years)</span><input style={input} type="number" value={v.years} onChange={set('years')} /></div>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Monthly costs</h2>
          <div style={grid}>
            <div><span style={label}>Property tax (per year)</span><input style={input} type="number" value={v.tax} onChange={set('tax')} /></div>
            <div><span style={label}>Homeowners insurance (per year)</span><input style={input} type="number" value={v.insurance} onChange={set('insurance')} /></div>
            <div><span style={label}>HOA</span><div style={{ display: 'flex', gap: 8 }}><input style={{ ...input, flex: 1 }} type="number" value={v.hoa} onChange={set('hoa')} placeholder="0" /><select style={{ ...input, width: 'auto' }} value={v.hoaPeriod} onChange={set('hoaPeriod')}><option value="month">per month</option><option value="quarter">per quarter</option></select></div></div>
            <div><span style={label}>CDD (per year)</span><input style={input} type="number" value={v.cdd} onChange={set('cdd')} /></div>
            <div><span style={label}>PMI % per year (if under 20% down)</span><input style={input} type="number" step="0.01" value={v.pmiPct} onChange={set('pmiPct')} /></div>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Other monthly expenses</h2>
          <p style={muted}>Add anything else the buyer will pay each month, such as pool service or security monitoring.</p>
          {extras.map((e) => (
            <div key={e.id} style={{ ...row, alignItems: 'center' }}>
              <span>{e.label}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span>{money(num(e.amount))}</span>
                <button onClick={() => removeExtra(e.id)} title="Remove" style={{ background: 'none', border: '1px solid #d8c9a8', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: '#8C6D1F', fontWeight: 700 }}>✕</button>
              </span>
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end', marginTop: 12 }}>
            <div><span style={label}>Expense</span><input style={input} value={newExtra.label} onChange={(e) => setNewExtra({ ...newExtra, label: e.target.value })} placeholder="Pool service" /></div>
            <div><span style={label}>Per month ($)</span><MoneyInput style={input} value={newExtra.amount} onChange={(e) => setNewExtra({ ...newExtra, amount: e.target.value })} /></div>
            <button onClick={addExtra} style={btn}>Add expense</button>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Lee County property tax estimate</h2>
          <p style={muted}>Use the Lee County Property Appraiser&apos;s tax estimator for the address, then enter the annual tax above.</p>
          <a href="https://www.leepa.org/" target="_blank" rel="noopener noreferrer" style={{ ...btn, textDecoration: 'none', display: 'inline-block' }}>Open Lee County Property Appraiser</a>
        </div>

        <div style={card}>
          <h2 style={h2}>Collier County property tax estimate</h2>
          <p style={muted}>Look up the property tax for the address, then enter the annual tax above.</p>
          <a href="https://county-taxes.net/fl-collier/property-tax" target="_blank" rel="noopener noreferrer" style={{ ...btn, textDecoration: 'none', display: 'inline-block' }}>Open Collier County tax lookup</a>
        </div>

        <div style={card}>
          <p style={muted}>
            Estimates that don&apos;t use the current tax bill are only approximations. Actual property taxes depend on the buyer&apos;s exemption status, the purchase price, and the appraised value after the sale. For the most accurate figure, use the Lee County Property Appraiser&apos;s tax estimator.
          </p>
        </div>

        <div style={card}>
          <h2 style={h2}>Estimate</h2>
          {rateMissing && <p style={{ ...muted, color: '#8C6D1F' }}>Enter the interest rate to see the payment. Principal and interest are not calculated until a rate is entered.</p>}
          <div style={row}><span>Loan amount</span><span>{money(loan)}</span></div>
          <div style={row}><span>Principal and interest</span><span>{rateMissing ? '—' : money(pi)}</span></div>
          <div style={row}><span>Property tax</span><span>{money(taxMo)}</span></div>
          <div style={row}><span>Insurance</span><span>{money(insMo)}</span></div>
          <div style={row}><span>HOA</span><span>{money(hoaMo)}</span></div>
          <div style={row}><span>CDD</span><span>{money(cddMo)}</span></div>
          <div style={row}><span>PMI {down < 20 ? '' : '(not required at 20% down)'}</span><span>{money(pmiMo)}</span></div>
          {extras.map((e) => <div key={e.id} style={row}><span>{e.label}</span><span>{money(num(e.amount))}</span></div>)}
          <div style={bigRow}><span>Estimated monthly payment</span><span>{money(total)}</span></div>
          <div style={{ ...row, marginTop: 10 }}><span>Down payment (cash needed before closing costs)</span><span>{money(cashToClose)}</span></div>
          <div style={row}><span>Total interest over the loan</span><span>{money(totalInterest)}</span></div>
        </div>

        <ShareReport
          title="Monthly Payment Estimate"
          fileBaseName="Monthly payment estimate"
          defaultMessage={(client, channel, agent) => `Hi ${(client?.name || '').split(' ')[0]}, here is your monthly payment estimate for ${money(price)}. I've ${channel === 'text' ? 'linked' : 'attached'} it below. I'd love the opportunity to walk through it together.\n\n${agent.name}${agent.phone ? `\nCall or text: ${agent.phone}` : ''}`}
          logoSrc="/residence-exp-logo.png"
          height={1760 + extras.length * 70}
          details={[
            { label: 'Purchase price', value: money(price) },
            { label: 'Down payment', value: `${down}% (${money(cashToClose)})` },
            { label: 'Loan amount', value: money(loan) },
            { label: 'Interest rate (30-year fixed)', value: `${num(v.rate)}%` },
            { label: 'Loan term', value: `${num(v.years)} years` },
          ]}
          detailsTitle="Loan details"
          rowsTitle="Monthly breakdown"
          rows={[
            { label: 'Principal and interest', value: rateMissing ? '—' : money(pi) },
            { label: 'Property tax', value: money(taxMo) },
            { label: 'Homeowners insurance', value: money(insMo) },
            { label: 'HOA', value: money(hoaMo) },
            { label: 'CDD', value: money(cddMo) },
            { label: down < 20 ? 'PMI' : 'PMI (not required at 20% down)', value: money(pmiMo) },
            ...extras.map((e) => ({ label: e.label, value: money(num(e.amount)) })),
          ]}
          highlight={{ label: 'Estimated monthly payment', value: rateMissing ? 'Enter a rate' : money(total) }}
          note="Estimate only. Final payment depends on the lender, loan program, and actual taxes and insurance. Down payment shown is cash before closing costs."
        />
      </div>
    </div>
  );
}
